/**
 * Shape a raw `GET payment/me/statement` response into what the wallet screen
 * renders. The backend now computes the totals server-side and returns a single
 * flat, newest-first ledger:
 *
 *   { userId, earned, spent, withdrawn, pending, entries[] }
 *
 * where each entry is
 *   { direction:'IN'|'OUT', category:'earned'|'spent'|'withdrawn',
 *     amount (positive), type, counterpartyName, label, contractId, date,
 *     reference, receiptUrl }
 *
 * This is pure and unit-testable; it guards every field so a missing/garbage
 * payload degrades to zero totals + no rows rather than throwing. Each entry is
 * turned into a render-safe row carrying a resolved `title`, a stable `key`, and
 * the `category`/`direction` the tabbed list groups and signs by.
 */

/** Fallback titles derived from the ledger `type` when no name/label is present. */
const TYPE_TITLE_ID = {
  CHARGE_SETTLED: "WALLET_EVENT_SECURED",
  TRANSFER_RELEASED: "WALLET_EVENT_EARNED",
  CHARGE_PAID: "WALLET_EVENT_PAYMENT",
  PAYOUT_PAID: "WALLET_EVENT_PAID_TO_BANK",
  TRANSFER_REVERSED: "WALLET_EVENT_RETURNED",
};

const toAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.abs(n) : 0;
};

/**
 * Only surface a receipt link for an https:// URL. Blocks javascript:/data:
 * schemes from reaching an anchor href even if the backend ever returns one.
 */
const safeReceiptUrl = (value) =>
  typeof value === "string" && value.startsWith("https://") ? value : null;

/**
 * Resolve a display title: prefer the human counterparty/label the backend sent,
 * otherwise fall back to an i18n key derived from the ledger `type`. Returns
 * either `{ text }` (already-localized backend string) or `{ id }` (i18n key) so
 * the view can render the right one without hardcoding copy.
 */
const resolveTitle = (entry) => {
  const text = entry?.counterpartyName || entry?.label;
  if (text) return { text };
  const id = TYPE_TITLE_ID[entry?.type];
  if (id) return { id };
  // Ledger rows pass `event_type` through verbatim, so an unrecognized type would
  // otherwise render raw SCREAMING_CASE to the user. Fall back on the row's
  // DIRECTION, which is always known — never mislabel an outgoing row as a payment.
  return {
    id: entry?.direction === "OUT" ? "WALLET_EVENT_PAID_OUT" : "WALLET_EVENT_PAYMENT",
  };
};

/** Map one ledger entry to a stable, render-safe row. */
const toEntryRow = (entry, index) => ({
  key: entry?.reference || `${entry?.type || "entry"}-${index}`,
  type: entry?.type || "",
  direction: entry?.direction === "OUT" ? "OUT" : "IN",
  category: entry?.category || "",
  amount: toAmount(entry?.amount),
  contractId: entry?.contractId ?? null,
  title: resolveTitle(entry),
  date: entry?.date || null,
  receiptUrl: safeReceiptUrl(entry?.receiptUrl),
});

export const readStatement = (statement) => {
  const source =
    statement && typeof statement === "object" ? statement : {};
  const entries = Array.isArray(source.entries)
    ? source.entries.map(toEntryRow)
    : [];
  return {
    earned: toAmount(source.earned),
    spent: toAmount(source.spent),
    withdrawn: toAmount(source.withdrawn),
    pending: toAmount(source.pending),
    entries,
  };
};
